import { Calendar, Search, ChevronDown } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import type { UseFormRegister, FieldErrors, FieldValues, Path } from 'react-hook-form';

import FormInput from '../forms/FormInput';
import Button from '../ui/Button';

interface IDateRangeFilterProps<T extends FieldValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  dateFromName: Path<T>;
  dateToName: Path<T>;
  dateFrom: string;
  dateTo: string;
  onSearch: () => void;
  onClear: () => void;
  className?: string;
}

const DateRangeFilter = <T extends FieldValues>({
  register,
  errors,
  dateFromName,
  dateToName,
  dateFrom,
  dateTo,
  onSearch,
  onClear,
  className = '',
}: IDateRangeFilterProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isSearchDisabled = !dateFrom && !dateTo;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle search button click
  const handleSearchClick = () => {
    onSearch();
    setIsOpen(false); // Close dropdown after search
  };

  // Handle clear button click
  const handleClearClick = () => {
    onClear();
    setIsOpen(false); // Close dropdown after clear
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        ref={buttonRef}
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className='flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors bg-white border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme-500 focus:ring-offset-2'
        aria-haspopup='menu'
        aria-expanded={isOpen}
        aria-label='Date range filter'
      >
        <Calendar size={16} className='flex-shrink-0 text-gray-500' />
        <span className='text-sm font-medium'>Date range</span>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden='true'
        />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className='absolute top-full left-0 mt-2 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-20 p-3'>
          <div className='flex flex-col gap-2.5'>
            {/* Start Date */}
            <div className='[&_label]:text-sm [&_label]:mb-1 [&_label]:font-medium'>
              <FormInput<T>
                name={dateFromName}
                label='Date From'
                type='date'
                placeholder='Select Start Date'
                register={register}
                errors={errors}
                className='!text-sm !py-1.5 !px-2.5 !h-9 !w-full [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4'
                containerClassName='text-sm'
              />
            </div>

            {/* End Date */}
            <div className='[&_label]:text-sm [&_label]:mb-1 [&_label]:font-medium'>
              <FormInput<T>
                name={dateToName}
                label='Date To'
                type='date'
                placeholder='Select End Date'
                register={register}
                errors={errors}
                validation={{
                  validate: {
                    dateAfterFrom: (value) => {
                      if (dateFrom && value && new Date(value) < new Date(dateFrom)) {
                        return 'End date must be after start date';
                      }
                      return true;
                    },
                  },
                }}
                className='!text-sm !py-1.5 !px-2.5 !h-9 !w-full [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4'
                containerClassName='text-sm'
              />
            </div>

            {/* Search and Clear Buttons */}
            <div className='flex justify-center gap-2 pt-1'>
              <Button
                type='button'
                variant='primary'
                onClick={handleSearchClick}
                disabled={isSearchDisabled}
                leftIcon={<Search className='w-3.5 h-3.5' />}
                className='min-w-[90px] !text-sm !py-1.5 !px-3 !h-8'
              >
                Search
              </Button>
              <Button
                type='button'
                variant='secondary'
                onClick={handleClearClick}
                className='min-w-[90px] !text-sm !py-1.5 !px-3 !h-8'
              >
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
